import React, { Component, ErrorInfo, ReactNode } from 'react';
import ErrorTracker from '../services/errorTracker';
import Logger from '../services/logger';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  resetOnPropsChange?: boolean;
  resetKeys?: Array<string | number>;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  retryCount: number;
}

class ErrorBoundary extends Component<Props, State> {
  private retryTimeoutId: NodeJS.Timeout | null = null;

  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: 0
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    // Handle ChunkLoadError with page reload
    if (error?.name === 'ChunkLoadError' || error?.message?.includes('Loading chunk') || error?.message?.includes('Failed to fetch dynamically imported module')) {
      // Reload page immediately for ChunkLoadError
      setTimeout(() => window.location.reload(), 500);
      return {
        hasError: false, // Don't show error UI, just reload
        error: null
      };
    }
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Handle ChunkLoadError with page reload
    if (error?.name === 'ChunkLoadError' || error?.message?.includes('Loading chunk') || error?.message?.includes('Failed to fetch dynamically imported module')) {
      Logger.warn('ChunkLoadError detected in ErrorBoundary, reloading page', { error: error.message });
      setTimeout(() => window.location.reload(), 500);
      return;
    }

    // Log error to ErrorTracker
    ErrorTracker.trackError(error, 'React Error Boundary', {
      component: 'ErrorBoundary',
      errorInfo: errorInfo.componentStack,
      retryCount: this.state.retryCount
    });

    // Log to console for development
    Logger.error('Error Boundary caught an error', error, {
      componentStack: errorInfo.componentStack,
      retryCount: this.state.retryCount
    });

    this.setState({
      error,
      errorInfo
    });

    // Call custom error handler if provided
    this.props.onError?.(error, errorInfo);
  }

  componentDidUpdate(prevProps: Props) {
    const { resetKeys, resetOnPropsChange } = this.props;
    const { hasError } = this.state;

    // Reset error boundary when resetKeys change
    if (hasError && resetKeys && prevProps.resetKeys) {
      const hasResetKeyChanged = resetKeys.some((key, index) => 
        key !== prevProps.resetKeys?.[index]
      );
      
      if (hasResetKeyChanged) {
        this.resetErrorBoundary();
      }
    }

    // Reset error boundary when resetOnPropsChange is true and props change
    if (hasError && resetOnPropsChange && prevProps.children !== this.props.children) {
      this.resetErrorBoundary();
    }
  }

  componentWillUnmount() {
    if (this.retryTimeoutId) {
      clearTimeout(this.retryTimeoutId);
    }
  }

  resetErrorBoundary = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: 0
    });
  };

  handleRetry = () => {
    const { retryCount } = this.state;
    const maxRetries = 3;

    if (retryCount < maxRetries) {
      this.setState(prevState => ({
        retryCount: prevState.retryCount + 1
      }));

      // Clear any existing timeout
      if (this.retryTimeoutId) {
        clearTimeout(this.retryTimeoutId);
      }

      // Retry after a short delay
      this.retryTimeoutId = setTimeout(() => {
        this.resetErrorBoundary();
      }, 1000 * (retryCount + 1)); // Exponential backoff
    } else {
      // Max retries reached, reset to initial state
      this.resetErrorBoundary();
    }
  };

  render() {
    const { hasError, error, retryCount } = this.state;
    const { children, fallback } = this.props;

    if (hasError) {
      // Use custom fallback if provided
      if (fallback) {
        return fallback;
      }

      // Default error UI
      return (
        <div style={{
          padding: '20px',
          border: '1px solid #ff6b6b',
          borderRadius: '8px',
          backgroundColor: '#fff5f5',
          margin: '10px',
          fontFamily: 'Arial, sans-serif'
        }}>
          <div style={{ textAlign: 'center' }}>
            <h2 style={{ color: '#d63031', marginBottom: '10px' }}>
              ⚠️ Something went wrong
            </h2>
            <p style={{ color: '#636e72', marginBottom: '20px' }}>
              We're sorry, but something unexpected happened. This has been reported to our team.
            </p>
            
            {process.env.NODE_ENV === 'development' && error && (
              <details style={{ 
                textAlign: 'left', 
                marginBottom: '20px',
                backgroundColor: '#f8f9fa',
                padding: '10px',
                borderRadius: '4px',
                border: '1px solid #dee2e6'
              }}>
                <summary style={{ cursor: 'pointer', fontWeight: 'bold' }}>
                  Error Details (Development Only)
                </summary>
                <pre style={{ 
                  marginTop: '10px', 
                  fontSize: '12px', 
                  color: '#d63031',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word'
                }}>
                  {error.toString()}
                  {this.state.errorInfo?.componentStack}
                </pre>
              </details>
            )}

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
              <button
                onClick={this.handleRetry}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#1976d2',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                {retryCount > 0 ? `Retry (${retryCount}/3)` : 'Try Again'}
              </button>
              
              <button
                onClick={() => window.location.reload()}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Reload Page
              </button>
            </div>

            {retryCount >= 3 && (
              <p style={{ 
                color: '#e17055', 
                fontSize: '12px', 
                marginTop: '10px',
                fontStyle: 'italic'
              }}>
                Multiple retry attempts failed. Please reload the page or contact support.
              </p>
            )}
          </div>
        </div>
      );
    }

    return children;
  }
}

export default ErrorBoundary;
